// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the same domain and port as the frontend
  return `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const parseErrorResponse = (res, contentType) => {
  const createError = data => {
    let e = new Error(`API request failed with status ${res.status}`);
    e.status = res.status;
    e.statusText = res.statusText;
    e = Object.assign(e, data);
    throw e;
  };

  if (contentType === 'application/json') {
    return res.json().then(createError);
  }
  if (contentType === 'application/transit+json') {
    return res
      .text()
      .then(deserialize)
      .then(createError);
  }
  return res.text().then(body => createError({ body }));
};

const handleRequest = (url, options) => {
  return window.fetch(url, options).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return parseErrorResponse(res, contentType);
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

const post = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: serialize(body),
  };
  return handleRequest(url, options);
};

const get = (path, params = {}, headers = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const queryParams = new URLSearchParams(params).toString();
  const options = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
      ...headers,
    },
  };

  return handleRequest(`${url}${queryParams ? '?' : ''}${queryParams}`, options);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

export const initiatePrivilegedWithPaypal = body => {
  return post('/api/paypal/initiate-privileged', body);
};

export const transitionPrivilegedWithPaypal = body => {
  return post('/api/paypal/transition-privileged', body);
};

export const initiateSpeculativeWithPaypal = body => {
  return post('/api/paypal/initiate-spec', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const createUserWithPassword = body => {
  return post('/api/auth/signup', body);
};

export const getTurnstileConfig = () => {
  return get('/api/auth/turnstile-config');
};

export const startSubscription = body => {
  return post('/api/start-subscription', body);
};

export const revokeSubscription = body => {
  return post('/api/cancel-subscription', body);
};

export const initiateRefund = body => {
  return post('/api/initiate-refund', body);
};

export const sendSendGridNotification = body => {
  return post('/api/sendgrid/send-notification', body);
};

export const fetchListingAvailabilities = body => {
  return post('/api/integration/fetch-availabilities', body);
};

export const fetchListingAvailabilties = fetchListingAvailabilities;

// PayPalApi
export const PayPalApi = {
  getOnboardingLink: body => post('/api/paypal/onboarding-link', body),
  getMerchantOnboardingStatus: () => get('/api/paypal/merchant-signup-status'),
  authorizeOrder: (orderId, body) =>
    post(`/api/paypal/orders/${encodeURIComponent(orderId)}/authorize`, body),
  confirmBooking: transactionId =>
    post(`/api/paypal/transactions/${encodeURIComponent(transactionId)}/confirm`, {}),
  cancelBooking: (transactionId, body) =>
    post(`/api/paypal/transactions/${encodeURIComponent(transactionId)}/cancel`, body),
};
